import { type MaybePromise, RequestError } from '@vartion/ui'
import { debounce, isEqual } from 'lodash-es'
import type { MaybeRefOrGetter } from 'vue'

import { restartableWatch } from '~/helpers/index.ts'
import { sentry } from '~/services/sentry.ts'
import type { Meta, SearchResponse } from '~/types.ts'

export interface FetchOptions<TData> {
  /** The input data to watch for changes. It is send in the request. */
  inputs?: MaybeRefOrGetter<unknown>
  /** Trigger the watcher immediately. */
  immediate?: boolean
  /** Watch deeply. */
  deep?: boolean | number
  /** The debounce wait time between requests. */
  debounce?: number
  /** The HTTP request method to use. */
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete'
  /** Callback before fetching, after adding pagination. `cancel()` skips the new request and aborts the current one; `cancel({ abort: false })` preserves the current request. */
  onBeforeFetch?: OnBeforeFetchCallback<TData | undefined>
  /** Callback that is called when the data is fetched. The `data` returned from the composable is not updated yet. */
  onFetched?: OnFetchedCallback<TData>
  /** Callback that is called when a `RequestError` occurs. AbortErrors are not passed to this callback. */
  onError?: (error: RequestError) => void

  /**
   * Adds page and per_page properties to the request data.
   * Sets the data to an array.
   * Unpacks the data and meta properties from the response.
   */
  pagination?: boolean

  /** Provide the default value of the returned data. */
  default?: TData

  /** Defaults to assign to the meta object. `currentPage` only works when `immediate` is `false`. */
  meta?: Partial<Pick<Meta, 'pageSize' | 'currentPage'>>

  /** Appends data from additional fetches when `meta.currentPage` changes. Data will still be reset when `inputs` change. Default `false`. */
  appendData?: false
}

// @ts-expect-error - The data is unpacked when pagination = true.
export interface PaginationFetchOptions<TData extends SearchResponse> extends FetchOptions<TData> {
  onBeforeFetch?: OnBeforeFetchCallback<TData['data']>
  pagination: true
  onFetched?: (responseData: TData['data']) => MaybePromise<any>

  /** Appends data from additional fetches when `meta.currentPage` changes. Data will still be reset when `inputs` change. Default `false`. */
  appendData?: boolean
}

interface UseFetchReturn<TData> {
  data: Ref<TData>
  meta: Meta
  loading: Ref<boolean>
  fetch: () => any
  key: string
  /** Navigate to the page containing a specific record by its id value. Optionally provide a different field key to use for pagination. */
  paginateTo: (value: string | number, key?: string) => Promise<void>
}

type OnBeforeFetchCallback<TData> = (requestData: any, cancel: (options?: { abort?: boolean }) => void, data: Ref<TData>) => MaybePromise<any>
type OnFetchedCallback<ResponseData> = (responseData: ResponseData) => MaybePromise<any>

let counter = 0

export function assignMetaFromResponseData(meta: Meta, responseData: SearchResponse) {
  Object.assign(meta, {
    currentPage: responseData.meta.current_page,
    pageSize: responseData.meta.per_page,
    pageCount: responseData.meta.last_page,
    to: responseData.meta.to,
    from: responseData.meta.from,
    total: responseData.meta.total,
  })
}

/**
 * Automatically updates data when the input data changes.
 * @param url The URL to send the request to.
 * @param options The options to use.
 * @returns The data and loading state.
 */
export function useFetch<TData>(url: MaybeRefOrGetter<string>, options: FetchOptions<TData> & { default: TData }): UseFetchReturn<TData>
// @ts-expect-error - We know that `data` and `meta` are properties on the response when pagination = true.
export function useFetch<TData>(url: MaybeRefOrGetter<string>, options: PaginationFetchOptions<TData>): UseFetchReturn<TData['data']>
export function useFetch<TData>(url: MaybeRefOrGetter<string>, options: FetchOptions<TData>): UseFetchReturn<TData | undefined>
export function useFetch<TData>(url: MaybeRefOrGetter<string>, options: FetchOptions<TData> = {}): any {
  const loading = ref(false)
  const data = ref<TData | undefined>(options.default)
  if (options.pagination) data.value = []
  const meta = reactive<Meta>(createMeta())
  Object.assign(meta, options.meta)
  const key = `useFetch:${counter++}`
  const callbacks = {
    onBeforeFetch: [] as OnBeforeFetchCallback<TData>[],
    onFetched: [] as OnFetchedCallback<TData>[],
  }

  // Pagination helper state.
  let paginateToValue = undefined as string | number | undefined
  let paginateToKey = undefined as string | undefined

  function onBeforeFetch(callback: OnBeforeFetchCallback<TData>) {
    callbacks.onBeforeFetch.push(callback)
  }

  if (options.onBeforeFetch) {
    onBeforeFetch(options.onBeforeFetch)
  }

  function onFetched(callback: OnFetchedCallback<TData>) {
    callbacks.onFetched.push(callback)
  }

  if (options.onFetched) {
    onFetched(options.onFetched)
  }

  async function onInputsChanged(inputs: unknown, appendData = false) {
    loading.value = true
    try {
      let requestData = copyObject(inputs ?? {}) as Record<string, unknown>

      if (options.pagination) {
        requestData.per_page = meta.pageSize
        requestData.page = meta.currentPage

        if (paginateToValue !== undefined) {
          requestData.paginate_to_value = paginateToValue
        }
        if (paginateToKey !== undefined) {
          requestData.paginate_to_key = paginateToKey
        }
      }

      for (const callback of callbacks.onBeforeFetch) {
        let cancelled = false

        const result = await callback(
          requestData,
          ({ abort = true } = {}) => {
            cancelled = true

            if (abort) {
              api.abort(key)
            }
          },
          data as Ref<TData>,
        )

        if (cancelled) {
          loading.value = false

          return
        }

        if (result) {
          requestData = result
        }
      }

      const method = options.method ?? 'post'

      const response =
        method === 'get' || method === 'delete' ? await api[method](toValue(url), { params: requestData, key }) : await api[method](toValue(url), requestData, { key })

      if (options.pagination && response.data && typeof response.data === 'object' && 'meta' in response.data && 'data' in response.data) {
        if (response.data.meta && Object.keys(response.data.meta).length > 0) {
          stop()
          // Transform Laravel pagination to Vartion UI pagination format.
          assignMetaFromResponseData(meta, response.data)
          start()
        }
        if (meta.currentPage > meta.pageCount) {
          // This can happen when a user changes the page size to a larger value, which reduces the total number of pages. In this case, we navigate to the last page.
          meta.currentPage = meta.pageCount
        }

        paginateToValue = undefined
        paginateToKey = undefined

        response.data = response.data.data

        if (appendData) {
          response.data = Array.from(data.value).concat(response.data)
        }
      }

      for (const callback of callbacks.onFetched) {
        const result = await callback(response.data)
        if (result) {
          response.data = result
        }
      }
      data.value = response.data
      loading.value = false
    } catch (error) {
      if (!isAbortError(error)) {
        loading.value = false

        if (error instanceof RequestError && options.onError) {
          options.onError(error)
        }
      }

      sentry.captureException(error)
      if (!$env.sentry.dsn) {
        console.error(error)
      }
    }
  }

  async function fetch() {
    await onInputsChanged(toValue(options.inputs))
  }

  async function paginateTo(value: string | number, key?: string) {
    paginateToValue = value
    if (key !== undefined) {
      paginateToKey = key
    }
    await fetch()
  }

  const inputs = computed((oldValue) => {
    const inputs = toValue(options.inputs)

    if (isEqual(inputs, oldValue)) {
      return oldValue
    }

    return inputs
  })

  const debouncedOnInputsChanged = options.debounce ? debounce(onInputsChanged, options.debounce, { leading: true, trailing: true }) : onInputsChanged

  const { stop, start } = restartableWatch([() => meta.currentPage, () => meta.pageSize], ([page], [oldPage]) => {
    const appendData = options.appendData && page === oldPage + 1
    debouncedOnInputsChanged(inputs.value, appendData)
  })

  watch(
    [inputs, () => toValue(url)],
    ([inputs]) => {
      stop()
      meta.currentPage = 1
      start()
      debouncedOnInputsChanged(inputs)
    },
    {
      immediate: options.immediate,
      deep: options.deep,
    },
  )

  const result = {
    data: data as any,
    meta,
    loading,
    fetch,
    key,
    paginateTo,
  } as UseFetchReturn<TData>

  return result
}
